import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"

export interface AuthCryptoData {
  success: boolean
  encryptionMode: "standard" | "private"
  /** Another household member holds the household key: claim it, never mint one. */
  householdPrivate?: boolean
  encryptedVerifier: string | null
  encryptedRecoveryKey: string | null
  kdfVersion: number | null
  kdfSalt: string | null
  wrappedDek: string | null
}

/**
 * Server-side compare-and-swap for #184: overwriting stored key material (verifier / wrapped
 * DEK / recovery key) must echo what is currently stored. Read it fresh at write time so a
 * stale tab or a racing device is refused instead of silently clobbering the keys.
 * Returns undefined for first-time setup (nothing stored yet).
 */
async function fetchCurrentVerifier(): Promise<string | undefined> {
  const cur = await api.get<AuthCryptoData>("/auth-crypto")
  return cur.wrappedDek ?? cur.encryptedVerifier ?? cur.encryptedRecoveryKey ?? undefined
}

async function withCurrentVerifier<T extends object>(
  body: T,
): Promise<T & { currentVerifier?: string }> {
  const currentVerifier = await fetchCurrentVerifier()
  return currentVerifier ? { ...body, currentVerifier } : body
}

export function useAuthCrypto() {
  return useQuery({
    queryKey: ["auth-crypto"],
    queryFn: () => api.get<AuthCryptoData>("/auth-crypto"),
  })
}

export function useSetRecoveryKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (encryptedRecoveryKey: string) =>
      api.post("/auth-crypto/recovery", await withCurrentVerifier({ encryptedRecoveryKey })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth-crypto"] }),
  })
}

export function useUpgradeKdf() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { kdfVersion: number; kdfSalt: string; wrappedDek: string }) =>
      api.post("/auth-crypto/upgrade", await withCurrentVerifier(body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth-crypto"] }),
  })
}

export function useSetEncryptionMode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      encryptionMode: "standard" | "private"
      encryptedVerifier?: string
      encryptedRecoveryKey?: string
      kdfVersion?: number
      kdfSalt?: string
      wrappedDek?: string
      // Only for leaving private mode, after the client has decrypted every encrypted row (#183).
      confirmDecrypted?: boolean
      reencryptedCount?: number
    }) => {
      const writesKeys =
        body.encryptedVerifier !== undefined ||
        body.encryptedRecoveryKey !== undefined ||
        body.kdfVersion !== undefined ||
        body.kdfSalt !== undefined ||
        body.wrappedDek !== undefined
      return api.patch("/auth-crypto/mode", writesKeys ? await withCurrentVerifier(body) : body)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth-crypto"] }),
  })
}
